import { apiClient } from "@/services/apiClient";

// ===========================
// Types
// ===========================

export interface M365Group {
    id: string;
    displayName: string;
    description?: string;
    visibility?: string;
    groupTypes?: string[];
}

export interface M365GroupMember {
    id: string;
    displayName: string;
    mail?: string;
}

interface RawGroupMember {
    id?: string | number;
    aadObjectId?: string;
    displayName?: string;
    mail?: string;
    email?: string;
}

function normalizeGroupMember(member: RawGroupMember): M365GroupMember {
    const normalizedId = (member.aadObjectId ?? String(member.id ?? "")).trim();
    return {
        id: normalizedId,
        displayName: member.displayName ?? "",
        mail: member.mail ?? member.email ?? undefined,
    };
}

export interface SearchResults {
    groups: M365Group[];
}


// ===========================
// My Groups
// ===========================

export async function getMyGroups(): Promise<M365Group[]> {
    const res = await apiClient.get<M365Group[]>("/api/group/my-groups");
    return res.data;
}

export async function getMyMemberGroupsCount(): Promise<number> {
    const res = await apiClient.get<{ count: number }>("/api/group/my-member-count");
    return res.data.count;
}

export async function getMyOwnedGroupsCount(): Promise<number> {
    const res = await apiClient.get<{ count: number }>("/api/group/my-owned-count");
    return res.data.count;
}


// ===========================
// Group Search
// ===========================

export async function searchGroups(query: string): Promise<SearchResults> {
    const res = await apiClient.get<SearchResults>(`/api/group/search?query=${encodeURIComponent(query)}`);
    return res.data;
}


// ===========================
// Group Details
// ===========================

export async function getGroupDetails(groupId: string): Promise<M365Group> {
    const res = await apiClient.get<M365Group>(`/api/group/${groupId}`);
    return res.data;
}

export async function getGroupMembers(groupId: string): Promise<M365GroupMember[]> {
    const res = await apiClient.get<RawGroupMember[]>(`/api/group/${groupId}/members`);
    return res.data.map(normalizeGroupMember).filter((m) => m.id.length > 0);
}

export async function getGroupOwners(groupId: string): Promise<M365GroupMember[]> {
    const res = await apiClient.get<RawGroupMember[]>(`/api/group/${groupId}/owners`);
    return res.data.map(normalizeGroupMember).filter((m) => m.id.length > 0);
}

export async function getGroupSite(groupId: string): Promise<any> {
    const res = await apiClient.get(`/api/group/${groupId}/site`);
    return res.data;
}

export async function getGroupConversations(groupId: string) {
    const res = await apiClient.get(`/api/group/${groupId}/conversations`);
    return res.data;
}

export async function getGroupTeamsChannels(groupId: string) {
    const res = await apiClient.get(`/api/group/${groupId}/teams/channels`);
    return res.data;
}


// ===========================
// Group Actions (Optional)
// ===========================
// These may not exist yet but are common for admin functionality.
// Uncomment as needed.

/*
export async function addGroupMember(groupId: string, userId: string) {
    const response = await apiClient.post(`/group/${groupId}/members`, { userId });
    return response.data;
}

export async function removeGroupMember(groupId: string, userId: string) {
    const response = await apiClient.delete(`/group/${groupId}/members/${userId}`);
    return response.data;
}
*/

export default {
    getMyGroups,
    getMyMemberGroupsCount,
    getMyOwnedGroupsCount,
    searchGroups,
    getGroupDetails,
    getGroupMembers,
    getGroupOwners,
    getGroupSite,
    getGroupConversations,
    getGroupTeamsChannels
};
